import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import {
  loadTasksForDate, loadTasksForRange, loadIncompleteTasksForDate,
  createTask, updateTask, toggleTaskComplete, deleteTask,
  rolloverTask,
  getTodaysProgress, generatePlannerInsight,
  DEFAULT_CATEGORIES,
  tomorrowLocalDateStr, yesterdayLocalDateStr, pastNDaysRange,
} from '../plannerData';
import { todayLocalDateStr } from '../lifeAdminData';
import Card from '../../design-system/Card';
import EmptyState from '../../components/EmptyState';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import TaskRow from '../../components/TaskRow';
import { colors, spacing, radius, fontStack } from '../../design-system/tokens';

/**
 * /system/planner — Phase 4D rebuild.
 * Same data layer (plannerData.js), light-theme UI per spec.
 */
export default function Planner() {
  const [userId, setUserId] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  const [tomorrowTasks, setTomorrowTasks] = useState([]);
  const [yesterdayIncomplete, setYesterdayIncomplete] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [view, setView] = useState('home'); // home | history | insight
  const [insightResult, setInsightResult] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState(null);

  const [editTarget, setEditTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const today = todayLocalDateStr();
  const tomorrow = tomorrowLocalDateStr();
  const yesterday = yesterdayLocalDateStr();
  const todayDateLong = useMemo(
    () => new Date(`${today}T00:00:00`).toLocaleDateString('en-GB', { month: 'long', day: 'numeric' }),
    [today]
  );
  const tomorrowDateLong = useMemo(
    () => new Date(`${tomorrow}T00:00:00`).toLocaleDateString('en-GB', { month: 'long', day: 'numeric' }),
    [tomorrow]
  );

  /* MOUNT */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data: authData } = await supabase.auth.getUser();
        const user = authData?.user;
        if (cancelled) return;
        if (!user) { setLoadError('Not signed in'); return; }
        setUserId(user.id);
        const [tToday, tTomorrow, tYestIncomplete] = await Promise.all([
          loadTasksForDate(user.id, today).catch(() => []),
          loadTasksForDate(user.id, tomorrow).catch(() => []),
          loadIncompleteTasksForDate(user.id, yesterday).catch(() => []),
        ]);
        if (cancelled) return;
        setTodayTasks(tToday);
        setTomorrowTasks(tTomorrow);
        setYesterdayIncomplete(tYestIncomplete);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Could not load Planner');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAll = async () => {
    if (!userId) return;
    const [tToday, tTomorrow, tYestIncomplete] = await Promise.all([
      loadTasksForDate(userId, today).catch(() => []),
      loadTasksForDate(userId, tomorrow).catch(() => []),
      loadIncompleteTasksForDate(userId, yesterday).catch(() => []),
    ]);
    setTodayTasks(tToday);
    setTomorrowTasks(tTomorrow);
    setYesterdayIncomplete(tYestIncomplete);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 1800);
  };

  /* HANDLERS */

  const handleCreate = async ({ date, title, priority, category, estimated_duration_min }) => {
    try {
      await createTask({ user_id: userId, date, title, priority, category, estimated_duration_min });
      await refreshAll();
      showToast('Task added');
    } catch (err) {
      showToast(err.message || 'Save failed', 'error');
    }
  };

  const handleToggle = async (task) => {
    try {
      await toggleTaskComplete(task.id, !task.is_completed);
      await refreshAll();
    } catch (err) { showToast(err.message || 'Save failed', 'error'); }
  };

  const handleDelete = async (task) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await deleteTask(task.id);
      await refreshAll();
      showToast('Deleted');
    } catch (err) { showToast(err.message || 'Delete failed', 'error'); }
  };

  const handleRollover = async (task) => {
    try {
      await rolloverTask(task.id, tomorrow);
      await refreshAll();
      showToast('Pushed to tomorrow');
    } catch (err) { showToast(err.message || 'Rollover failed', 'error'); }
  };

  const handleEditSave = async (taskId, fields) => {
    try {
      await updateTask(taskId, fields);
      await refreshAll();
      setEditTarget(null);
      showToast('Saved');
    } catch (err) { showToast(err.message || 'Save failed', 'error'); }
  };

  const handleGenerateInsight = async () => {
    setView('insight');
    setInsightResult(null);
    setInsightError(null);
    setInsightLoading(true);
    try {
      const range = pastNDaysRange(7);
      const tasks = await loadTasksForRange(userId, range.start, range.end).catch(() => []);
      const result = await generatePlannerInsight({ tasks, todayDateStr: today });
      setInsightResult(result);
    } catch (err) {
      setInsightError(err.message || 'Could not generate insight');
    } finally {
      setInsightLoading(false);
    }
  };

  /* RENDER */

  if (loadError && !userId) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <ErrorState title="Couldn't load Planner" message={loadError} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', fontFamily: fontStack }}>
      <Header
        view={view}
        onBack={() => setView('home')}
        onHistory={() => setView('history')}
        onInsight={handleGenerateInsight}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg, marginTop: spacing.lg }}>
        {view === 'home' && (
          <>
            {/* Yesterday's incomplete banner */}
            {yesterdayIncomplete.length > 0 && (
              <RolloverBanner
                count={yesterdayIncomplete.length}
                onRolloverAll={async () => {
                  for (const t of yesterdayIncomplete) {
                    try { await rolloverTask(t.id, today); } catch (_) { /* skip */ }
                  }
                  await refreshAll();
                  showToast('Pulled forward');
                }}
                onDismiss={async () => {
                  // No persistent dismissal — just hide locally
                  setYesterdayIncomplete([]);
                }}
              />
            )}

            <div className="planner-day-grid">
              <DayCard
                date={today}
                title={`Today · ${todayDateLong}`}
                tasks={todayTasks}
                loading={loading && todayTasks.length === 0}
                error={loadError}
                emptyTitle="Nothing planned yet."
                emptyMessage="What's important today?"
                onCreate={(t) => handleCreate({ ...t, date: today })}
                onToggle={handleToggle}
                onEdit={setEditTarget}
                onDelete={handleDelete}
                onRollover={handleRollover}
              />
              <DayCard
                date={tomorrow}
                title={`Tomorrow · ${tomorrowDateLong}`}
                tasks={tomorrowTasks}
                loading={loading && tomorrowTasks.length === 0}
                error={loadError}
                emptyTitle="Plan ahead."
                emptyMessage="Drop tomorrow's first task in here."
                onCreate={(t) => handleCreate({ ...t, date: tomorrow })}
                onToggle={handleToggle}
                onEdit={setEditTarget}
                onDelete={handleDelete}
                onRollover={null}
              />
              <style>{`
                .planner-day-grid {
                  display: grid;
                  grid-template-columns: 1fr 1fr;
                  gap: 16px;
                }
                @media (max-width: 1024px) { .planner-day-grid { grid-template-columns: 1fr; } }
              `}</style>
            </div>

            {loadError && <ErrorState title="Some data couldn't load" message={loadError} onRetry={refreshAll} inline />}
          </>
        )}

        {view === 'history' && (
          <HistoryView userId={userId} onToggle={handleToggle} onDelete={handleDelete} />
        )}

        {view === 'insight' && (
          <InsightView
            loading={insightLoading}
            error={insightError}
            result={insightResult}
            onRetry={handleGenerateInsight}
          />
        )}
      </div>

      {editTarget && (
        <EditTaskModal
          task={editTarget}
          onClose={() => setEditTarget(null)}
          onSave={handleEditSave}
        />
      )}

      {toast && <Toast {...toast} />}
    </div>
  );
}

/* ================================================================
   Header
   ================================================================ */

function Header({ view, onBack, onHistory, onInsight }) {
  const inSubView = view !== 'home';
  return (
    <header style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <a
          href="/system"
          onClick={(e) => { if (inSubView) { e.preventDefault(); onBack(); } }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: colors.textSecondary,
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          ← {inSubView ? 'Back to Planner' : 'System'}
        </a>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: colors.textPrimary, letterSpacing: '-0.01em' }}>
          {viewTitle(view)}
        </h1>
        {!inSubView && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={onHistory} style={secondaryBtnStyle}>View History</button>
            <button type="button" onClick={onInsight} style={secondaryBtnStyle}>Get Planner Insight</button>
          </div>
        )}
      </div>
      {!inSubView && (
        <p style={{ margin: 0, color: colors.textSecondary, fontSize: 14 }}>
          A day on paper.
        </p>
      )}
    </header>
  );
}

function viewTitle(v) {
  return { home: 'Planner', history: 'History', insight: 'Planner Insight' }[v] || 'Planner';
}

/* ================================================================
   Day card
   ================================================================ */

function DayCard({
  date, title, tasks, loading, error,
  emptyTitle, emptyMessage,
  onCreate, onToggle, onEdit, onDelete, onRollover,
}) {
  const [adding, setAdding] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftPriority, setDraftPriority] = useState('medium');
  const [draftCategory, setDraftCategory] = useState('personal');
  const [draftDuration, setDraftDuration] = useState('');

  const reset = () => {
    setAdding(false);
    setDraftTitle('');
    setDraftPriority('medium');
    setDraftCategory('personal');
    setDraftDuration('');
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    const t = draftTitle.trim();
    if (!t) return;
    await onCreate({
      title: t,
      priority: draftPriority,
      category: draftCategory,
      estimated_duration_min: draftDuration ? Number(draftDuration) : undefined,
    });
    reset();
  };

  const progress = getTodaysProgress(tasks);
  const sortedTasks = [...(tasks || [])].sort((a, b) => {
    if (a.is_completed !== b.is_completed) return a.is_completed ? 1 : -1;
    return 0;
  });

  return (
    <Card padding={spacing.lg}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <h3 style={{
          margin: 0, fontSize: 16, fontWeight: 600, color: colors.textPrimary, fontFamily: fontStack,
        }}>{title}</h3>
        {tasks?.length > 0 && (
          <span style={{ fontSize: 12, color: colors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
            {progress.completed}/{progress.total}
          </span>
        )}
      </div>

      {/* Add task button / inline form */}
      {!adding ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          style={{
            ...primaryBtnStyle,
            padding: '8px 14px',
            minHeight: 36,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          + Add task
        </button>
      ) : (
        <form
          onSubmit={submit}
          style={{
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            padding: 12,
            marginBottom: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value.slice(0, 200))}
            placeholder="Task title"
            autoFocus
            maxLength={200}
            style={inputStyle}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select value={draftPriority} onChange={(e) => setDraftPriority(e.target.value)} style={{ ...inputStyle, width: 110 }}>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select value={draftCategory} onChange={(e) => setDraftCategory(e.target.value)} style={{ ...inputStyle, width: 130 }}>
              {DEFAULT_CATEGORIES.map((c) => <option key={c.name} value={c.name}>{c.label}</option>)}
            </select>
            <input
              type="number"
              min="1"
              inputMode="numeric"
              value={draftDuration}
              onChange={(e) => setDraftDuration(e.target.value)}
              placeholder="min"
              style={{ ...inputStyle, width: 80 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={reset} style={secondaryBtnStyle}>Cancel</button>
            <button type="submit" disabled={!draftTitle.trim()} style={{ ...primaryBtnStyle, opacity: draftTitle.trim() ? 1 : 0.5 }}>
              Add
            </button>
          </div>
        </form>
      )}

      {/* Task list */}
      {loading ? (
        <LoadingState rows={3} />
      ) : error ? (
        <ErrorState inline title="Couldn't load tasks" message={error} />
      ) : sortedTasks.length === 0 ? (
        <EmptyState title={emptyTitle} message={emptyMessage} />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {sortedTasks.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              onRollover={onRollover}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ================================================================
   Rollover banner — yesterday's incomplete pulled forward
   ================================================================ */

function RolloverBanner({ count, onRolloverAll, onDismiss }) {
  return (
    <Card padding={16} accentColor={colors.accentSystem}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontFamily: fontStack }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>
            {count} {count === 1 ? 'task' : 'tasks'} from yesterday still open
          </div>
          <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
            Pull them into today, or leave them in the past.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onDismiss} style={secondaryBtnStyle}>Dismiss</button>
          <button type="button" onClick={onRolloverAll} style={primaryBtnStyle}>Pull forward</button>
        </div>
      </div>
    </Card>
  );
}

/* ================================================================
   Edit modal
   ================================================================ */

function EditTaskModal({ task, onClose, onSave }) {
  const [title, setTitle] = useState(task.title || '');
  const [priority, setPriority] = useState(task.priority || 'medium');
  const [category, setCategory] = useState(task.category || 'personal');
  const [duration, setDuration] = useState(task.estimated_duration_min || '');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave(task.id, {
        title: title.trim(),
        priority,
        category,
        estimated_duration_min: duration ? Number(duration) : null,
      });
    } finally { setSaving(false); }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(17, 24, 39, 0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, zIndex: 200,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          background: '#fff',
          borderRadius: radius.lg,
          padding: spacing.lg,
          boxShadow: '0 24px 48px rgba(0,0,0,0.16)',
          fontFamily: fontStack,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.textPrimary, marginBottom: 12 }}>Edit task</h3>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Title">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 200))}
              maxLength={200}
              style={inputStyle}
              required
            />
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Field label="Priority">
              <select value={priority} onChange={(e) => setPriority(e.target.value)} style={inputStyle}>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </Field>
            <Field label="Category">
              <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
                {DEFAULT_CATEGORIES.map((c) => <option key={c.name} value={c.name}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Duration (min)">
              <input type="number" min="1" inputMode="numeric" value={duration} onChange={(e) => setDuration(e.target.value)} style={inputStyle} />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancel</button>
            <button type="submit" disabled={saving || !title.trim()} style={{ ...primaryBtnStyle, opacity: saving || !title.trim() ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ================================================================
   History view
   ================================================================ */

function HistoryView({ userId, onToggle, onDelete }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const range = pastNDaysRange(14);
        const data = await loadTasksForRange(userId, range.start, range.end);
        if (cancelled) return;
        setRows(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const byDate = useMemo(() => {
    const map = {};
    for (const r of rows) {
      if (!map[r.date]) map[r.date] = [];
      map[r.date].push(r);
    }
    return map;
  }, [rows]);

  const dates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  return (
    <Card padding={spacing.lg}>
      <h3 style={{
        margin: 0, fontSize: 14, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: colors.textSecondary, fontWeight: 600, fontFamily: fontStack,
      }}>
        History · last 14 days
      </h3>
      {loading ? (
        <div style={{ marginTop: 12 }}><LoadingState rows={4} /></div>
      ) : error ? (
        <div style={{ marginTop: 12 }}><ErrorState title="Couldn't load history" message={error} inline /></div>
      ) : dates.length === 0 ? (
        <EmptyState message="No tasks in the last two weeks." />
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {dates.map((d) => (
            <div key={d}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 6 }}>
                {new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', month: 'short', day: 'numeric' })}
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {byDate[d].map((t) => (
                  <TaskRow key={t.id} task={t} onToggle={onToggle} onDelete={onDelete} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ================================================================
   Insight view
   ================================================================ */

function InsightView({ loading, error, result, onRetry }) {
  return (
    <Card padding={spacing.lg} accentColor={colors.accentSystem}>
      <h3 style={{
        margin: 0, fontSize: 14, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: colors.textSecondary, fontWeight: 600, fontFamily: fontStack,
      }}>
        Planner Insight
      </h3>
      {loading ? (
        <div style={{ marginTop: 12 }}><LoadingState rows={4} /></div>
      ) : error ? (
        <div style={{ marginTop: 12 }}>
          <ErrorState title="Couldn't generate insight" message={error} onRetry={onRetry} inline />
        </div>
      ) : !result ? (
        <EmptyState message="No insight yet — generate one above." />
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <BulletList title="Observations" items={result.observations} />
          <BulletList title="Suggestions" items={result.suggestions} />
          {result.encouragement && (
            <p style={{
              margin: 0,
              padding: 12,
              backgroundColor: 'var(--bg-hover)',
              borderRadius: radius.sm,
              fontSize: 14,
              color: colors.textPrimary,
              fontStyle: 'italic',
              fontFamily: fontStack,
            }}>
              {result.encouragement}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

/* ================================================================
   Shared bits
   ================================================================ */

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: fontStack, flex: 1 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function BulletList({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textSecondary, fontWeight: 600, marginBottom: 8 }}>
        {title}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((it, i) => (
          <li key={i} style={{ fontSize: 14, color: colors.textPrimary, lineHeight: 1.5, paddingLeft: 12, borderLeft: `2px solid ${colors.border}` }}>
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Toast({ message, type }) {
  const isError = type === 'error';
  return (
    <div role="status" style={{
      position: 'fixed',
      bottom: 24,
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '10px 18px',
      borderRadius: 999,
      backgroundColor: isError ? 'var(--danger)' : colors.textPrimary,
      color: '#fff',
      fontSize: 13,
      fontWeight: 500,
      fontFamily: fontStack,
      boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
      zIndex: 100,
    }}>{message}</div>
  );
}

/* ---- shared button styles ---- */

const primaryBtnStyle = {
  padding: '10px 18px',
  minHeight: 40,
  borderRadius: 999,
  border: 'none',
  background: colors.primary,
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const secondaryBtnStyle = {
  padding: '10px 16px',
  minHeight: 40,
  borderRadius: 999,
  background: 'transparent',
  border: `1px solid ${colors.primary}`,
  color: colors.primary,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const inputStyle = {
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: 14,
  fontFamily: fontStack,
  color: colors.textPrimary,
  background: '#fff',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};
